import { create } from 'zustand';
import type { SapEnvironment, SapEnvironmentCreateDTO, SapEnvironmentUpdateDTO } from '@/domain/entities';
import { sapEnvironmentsApi } from '@/infrastructure/services/api-client';

interface SapEnvironmentsState {
    environments: SapEnvironment[];
    isLoading: boolean;
}

interface SapEnvironmentsActions {
    fetchEnvironments: () => Promise<void>;
    createEnvironment: (data: SapEnvironmentCreateDTO) => Promise<void>;
    updateEnvironment: (id: string, data: SapEnvironmentUpdateDTO) => Promise<void>;
    deleteEnvironment: (id: string) => Promise<void>;
}

type SapEnvironmentsStore = SapEnvironmentsState & SapEnvironmentsActions;

export const useSapEnvironmentsStore = create<SapEnvironmentsStore>()((set) => ({
    environments: [],
    isLoading: false,

    fetchEnvironments: async () => {
        set({ isLoading: true });
        try {
            const result = await sapEnvironmentsApi.list();
            set({
                environments: result.environments.map((env) => ({
                    ...env,
                    createdAt: new Date(env.createdAt),
                    updatedAt: new Date(env.updatedAt),
                })),
                isLoading: false,
            });
        } catch {
            set({ isLoading: false });
        }
    },

    createEnvironment: async (data: SapEnvironmentCreateDTO) => {
        const result = await sapEnvironmentsApi.create(data);
        const newEnv: SapEnvironment = {
            ...result.environment,
            createdAt: new Date(result.environment.createdAt),
            updatedAt: new Date(result.environment.updatedAt),
        };
        set((state) => ({
            environments: [newEnv, ...state.environments],
        }));
    },

    updateEnvironment: async (id: string, data: SapEnvironmentUpdateDTO) => {
        const result = await sapEnvironmentsApi.update(id, data);
        const updated: SapEnvironment = {
            ...result.environment,
            createdAt: new Date(result.environment.createdAt),
            updatedAt: new Date(result.environment.updatedAt),
        };
        set((state) => ({
            environments: state.environments.map((env) =>
                env.id === id ? updated : env,
            ),
        }));
    },

    deleteEnvironment: async (id: string) => {
        await sapEnvironmentsApi.delete(id);
        set((state) => ({
            environments: state.environments.filter((env) => env.id !== id),
        }));
    },
}));
